// src/pages/WorkHoursManagementPage.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
  deleteField,
  increment,
  arrayUnion,
  onSnapshot, // ✅ add
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import styles from "../styles/WorkHoursManagementPage.module.css";

/* =========================
   Helpers: Timezone + Dates
   ========================= */
const TZ = "Asia/Jerusalem";

const getTZDateParts = (d) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "long",
    hour12: false,
  }).formatToParts(d);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    weekdayName: map.weekday,
  };
};

const nowMonthInTZ = () => {
  const parts = getTZDateParts(new Date());
  return `${parts.year}-${String(parts.month).padStart(2, "0")}`;
};

const listMonthDatesTZ = (yyyy, mm) => {
  const dates = [];
  const startUTC = Date.UTC(yyyy, mm - 1, 1, 12, 0, 0);
  const endUTC = Date.UTC(yyyy, mm, 1, 12, 0, 0);
  for (let t = startUTC; t < endUTC; t += 24 * 60 * 60 * 1000) {
    const d = new Date(t);
    const p = getTZDateParts(d);
    dates.push(
      `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`
    );
  }
  return dates;
};

const isWorkingDayTZ = (yyyy, mm, dd) => {
  const atNoonUTC = new Date(Date.UTC(yyyy, mm - 1, dd, 12, 0, 0));
  const { weekdayName } = getTZDateParts(atNoonUTC);
  return weekdayName !== "Friday";
};

// Per-employee work schedule (stored on users/{uid}.workSchedule). `workingDays` holds weekday
// indices (0=Sunday … 6=Saturday) that count as working days; `requiredHoursPerDay` is the target.
const WEEKDAYS = [
  { idx: 0, short: "Sun", long: "Sunday" },
  { idx: 1, short: "Mon", long: "Monday" },
  { idx: 2, short: "Tue", long: "Tuesday" },
  { idx: 3, short: "Wed", long: "Wednesday" },
  { idx: 4, short: "Thu", long: "Thursday" },
  { idx: 5, short: "Fri", long: "Friday" },
  { idx: 6, short: "Sat", long: "Saturday" },
];
const WEEKDAY_NAME_TO_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
// Default working week when an employee has no custom schedule: everything except Friday — this
// preserves the app's original global rule (isWorkingDayTZ).
const DEFAULT_WORKING_DAYS = [0, 1, 2, 3, 4, 6];

const weekdayIndexForDateStr = (ds) => {
  const [y, m, d] = ds.split("-").map(Number);
  const atNoonUTC = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return WEEKDAY_NAME_TO_INDEX[getTZDateParts(atNoonUTC).weekdayName];
};

const getUserSchedule = (u) => {
  const ws = (u && u.workSchedule) || {};
  const workingDays = Array.isArray(ws.workingDays) && ws.workingDays.length ? ws.workingDays : DEFAULT_WORKING_DAYS;
  const requiredHoursPerDay = Number(ws.requiredHoursPerDay) || 0;
  return { workingDays, requiredHoursPerDay };
};

const fmtHHMM = (ms) => {
  if (ms == null || !isFinite(ms) || ms <= 0) return "0:00";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

const tsToDate = (ts) => (ts && typeof ts.toDate === "function" ? ts.toDate() : null);

const formatTimeInTZ = (date, timeZone = TZ) => {
  if (!date) return "";
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  return f.format(date);
};

const formatDisplayInTZ = (date, timeZone = TZ) => {
  if (!date) return "";
  const d = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  const t = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${d} ${t}`;
};

/* ===== DST-safe local->UTC conversion in TZ ===== */
const getTzOffsetMs = (timeZone, instant) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(instant);
  const map = {};
  for (const { type, value } of parts) map[type] = value;

  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUTC - instant.getTime();
};

const makeUtcFromLocalInTZ = (y, m, d, hh, mm, timeZone = TZ) => {
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
  const offsetMs = getTzOffsetMs(timeZone, guess);
  return new Date(Date.UTC(y, m - 1, d, hh, mm, 0) - offsetMs);
};

const makeAttendanceId = (userId, dateStr) => `${userId}_${dateStr}`;

const weekdayIndexFromName = (weekdayName) => {
  const map = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  return map[weekdayName] ?? 0;
};

/* =========================
   Small UI Helpers
   ========================= */
function Modal({ open, title, subtitle, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalTitle}>{title}</div>
            {subtitle ? <div className={styles.modalSubtitle}>{subtitle}</div> : null}
          </div>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>{children}</div>

        {footer ? <div className={styles.modalFooter}>{footer}</div> : null}
      </div>
    </div>
  );
}

function SectionButtons({ onOpen, pendingRequestsCount = 0 }) {
  return (
    <div className={styles.sectionGrid}>
      <button className={styles.sectionBtn} onClick={() => onOpen("requests")}>
        <div className={styles.sectionBtnTitle} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span>Requests</span>

          {pendingRequestsCount > 0 && (
            <span className={styles.notificationBadge} title="Pending requests">
              {pendingRequestsCount > 99 ? "99+" : pendingRequestsCount}
            </span>
          )}
        </div>

        <div className={styles.sectionBtnDesc}>Approve / Reject leave & hours requests</div>
      </button>

      <button className={styles.sectionBtn} onClick={() => onOpen("exceptions")}>
        <div className={styles.sectionBtnTitle}>Exceptions & Allowances</div>
        <div className={styles.sectionBtnDesc}>Vacation / Remote attendance by range</div>
      </button>

      <button className={styles.sectionBtn} onClick={() => onOpen("quick")}>
        <div className={styles.sectionBtnTitle}>Add / Edit Hours</div>
        <div className={styles.sectionBtnDesc}>Set check-in / check-out for any staff</div>
      </button>

      <button className={styles.sectionBtn} onClick={() => onOpen("bulk")}>
        <div className={styles.sectionBtnTitle}>Bulk Daily Hours</div>
        <div className={styles.sectionBtnDesc}>Fill hours for many days / many staff</div>
      </button>
    </div>
  );
}

/* =========================
   Component
   ========================= */
export default function WorkHoursManagementPage() {
  const { currentUser } = useAuth();

  const [month, setMonth] = useState(nowMonthInTZ());
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Page-level form modals
  const [activeForm, setActiveForm] = useState(null); // "requests"|"exceptions"|"quick"|"bulk"|null
  const openForm = (key) => setActiveForm(key);
  const closeForm = () => setActiveForm(null);

  // Edit existing record modal
  const [editing, setEditing] = useState(null);
  const [editInTime, setEditInTime] = useState("");
  const [editOutTime, setEditOutTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");

  // Exceptions
  const [exUserId, setExUserId] = useState("");
  const [exAction, setExAction] = useState("vacation");
  const [exStart, setExStart] = useState("");
  const [exEnd, setExEnd] = useState("");
  const [exVacationType, setExVacationType] = useState("Paid");
  const [exNote, setExNote] = useState("");
  const [exBusy, setExBusy] = useState(false);
  const [exMsg, setExMsg] = useState("");

  // Quick
  const [quickUserId, setQuickUserId] = useState("");
  const [quickDate, setQuickDate] = useState("");
  const [quickIn, setQuickIn] = useState("");
  const [quickOut, setQuickOut] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickMsg, setQuickMsg] = useState("");

  // Per-employee work schedule (working days + required hours/day)
  const [schedUserId, setSchedUserId] = useState("");
  const [schedUserName, setSchedUserName] = useState("");
  const [schedDays, setSchedDays] = useState(DEFAULT_WORKING_DAYS);
  const [schedHours, setSchedHours] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedMsg, setSchedMsg] = useState("");

  // Bulk
  const [bulkUserId, setBulkUserId] = useState("");
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");
  const [bulkIn, setBulkIn] = useState("09:00");
  const [bulkOut, setBulkOut] = useState("17:00");
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [bulkDays, setBulkDays] = useState([true, true, true, true, true, false, false]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");

  // Details modal
  const [detailsUserId, setDetailsUserId] = useState(null);

  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const [yyyy, mm] = useMemo(() => month.split("-").map(Number), [month]);

  /* =========================
     ✅ UPDATED: Requests (Unified)
     Collection: workHoursRequests
     ========================= */
  const [workReq, setWorkReq] = useState([]);
  const [reqTab, setReqTab] = useState("leave"); // leave|hours
  const [reqLoading, setReqLoading] = useState(false);
  const [reqMsg, setReqMsg] = useState("");
  const [managerNote, setManagerNote] = useState("");

  const leaveReq = useMemo(() => workReq.filter((r) => r.type === "leave"), [workReq]);
  const hoursReq = useMemo(() => workReq.filter((r) => r.type === "hours"), [workReq]);
  const extraReq = useMemo(() => workReq.filter((r) => r.type === "extra_hours"), [workReq]);

  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    setReqMsg("");
    try {
      const qReq = query(
        collection(db, "workHoursRequests"),
        where("status", "==", "Pending"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(qReq);
      setWorkReq(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to load requests.");
    } finally {
      setReqLoading(false);
    }
  }, []);

  // Auto load when modal opens
  useEffect(() => {
    if (activeForm === "requests") fetchRequests();
  }, [activeForm, fetchRequests]);

  /* =========================
     Fetch Users
     ========================= */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), orderBy("name", "asc")));
        if (!mounted) return;

        const list = snap.docs
          .map((d) => {
            const data = d.data();
            const active = data.active !== false;
            const notTerminated = data.employmentStatus !== "Terminated";
            if (!(active && notTerminated)) return null;

            return {
              id: d.id,
              name: data.name || data.email || d.id,
              email: data.email || "",
              role: data.role || "Employee",
              workSchedule: data.workSchedule || null,
            };
          })
          .filter(Boolean);

        setUsers(list);
      } catch (e) {
        console.error(e);
        setError("Failed to load users.");
      }
    })();
    return () => (mounted = false);
  }, []);

  const userMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  // ✅ Live pending count (shows on the Requests button without opening the modal)
  useEffect(() => {
    const qPending = query(collection(db, "workHoursRequests"), where("status", "==", "Pending"));

    const unsub = onSnapshot(
      qPending,
      (snap) => {
        if (!snap) return setPendingRequestsCount(0);
        setPendingRequestsCount(snap.size);
      },
      (err) => {
        console.error("pending requests count error", err);
        setPendingRequestsCount(0);
      }
    );

    return () => unsub();
  }, []);

  /* =========================
     Fetch Attendance
     ========================= */
  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const startDate = `${yyyy}-${String(mm).padStart(2, "0")}-01`;
      const dates = listMonthDatesTZ(yyyy, mm);
      const endDate = dates[dates.length - 1];

      const qAtt = query(collection(db, "attendance"), where("date", ">=", startDate), where("date", "<=", endDate));
      const snap = await getDocs(qAtt);
      setAttendance(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setError("Failed to load attendance.");
    } finally {
      setLoading(false);
    }
  }, [yyyy, mm]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const attIndex = useMemo(() => {
    const map = new Map();
    attendance.forEach((a) => {
      if (!a.userId) return;
      if (!map.has(a.userId)) map.set(a.userId, new Map());
      map.get(a.userId).set(a.date, a);
    });
    return map;
  }, [attendance]);

  const allMonthDates = useMemo(() => listMonthDatesTZ(yyyy, mm), [yyyy, mm]);

  const workingDates = useMemo(() => {
    return allMonthDates.filter((ds) => {
      const [y, m, d] = ds.split("-").map(Number);
      return isWorkingDayTZ(y, m, d);
    });
  }, [allMonthDates]);

  // Each month date paired with its weekday index, so per-employee working days can be derived
  // without recomputing the weekday for every user.
  const monthDaysMeta = useMemo(
    () => allMonthDates.map((ds) => ({ ds, wd: weekdayIndexForDateStr(ds) })),
    [allMonthDates]
  );

  /* =========================
     Summary (out - in only)
     ========================= */
  const summaryRows = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filteredUsers = users.filter((u) => !lower || u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));

    const out = filteredUsers.map((u) => {
      const perDate = attIndex.get(u.id) || new Map();

      // Working days come from this employee's own schedule (falls back to the default week).
      const schedule = getUserSchedule(u);
      const userWorkingDates = monthDaysMeta
        .filter((x) => schedule.workingDays.includes(x.wd))
        .map((x) => x.ds);

      let presentDays = 0;
      let vacationDays = 0;
      let totalWorkMs = 0;
      let completeDays = 0;

      userWorkingDates.forEach((ds) => {
        const rec = perDate.get(ds);

        if (rec?.status === "Vacation") {
          vacationDays += 1;
          return;
        }

        // A day that was cancelled or auto-marked absent for a missed checkout is NOT a present
        // day — it falls through to the absence count below (workingDays - present - vacation).
        if (rec?.cancelled || rec?.absentReason === "no_checkout") {
          return;
        }

        const inDate = tsToDate(rec?.checkInTime);
        const outDate = tsToDate(rec?.checkOutTime);

        if (!inDate) return;

        presentDays += 1;

        if (inDate && outDate) {
          const ms = outDate - inDate;
          if (ms > 0) {
            totalWorkMs += ms;
            completeDays += 1;
          }
        }
      });

      // Approved extra (compensatory) hours are stored on the day's attendance record as extraMs,
      // regardless of whether it was a scheduled working day, so sum them across the whole month.
      let extraMonthlyMs = 0;
      perDate.forEach((rec) => {
        extraMonthlyMs += Number(rec?.extraMs) || 0;
      });

      const workingDaysCount = userWorkingDates.length;
      const absences = Math.max(0, workingDaysCount - presentDays - vacationDays);
      const avgPerDayMs = completeDays > 0 ? totalWorkMs / completeDays : 0;
      const requiredHoursPerDay = schedule.requiredHoursPerDay;
      const requiredMonthlyMs = workingDaysCount * requiredHoursPerDay * 3600 * 1000;

      return {
        userId: u.id,
        name: u.name,
        email: u.email,
        role: u.role,
        workingDays: workingDaysCount,
        workingDayIdxs: schedule.workingDays,
        requiredHoursPerDay,
        requiredMonthlyMs,
        presentDays,
        vacationDays,
        absences,
        completeDays,
        extraHoursMs: extraMonthlyMs,
        totalHoursMs: totalWorkMs + extraMonthlyMs,
        avgHoursMs: avgPerDayMs,
      };
    });

    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [users, attIndex, monthDaysMeta, search]);

  /* =========================
     CSV Export (Summary)
     ========================= */
  const exportCSV = useCallback(() => {
    const header = ["Name", "Email", "Role", "Working Days", "Present Days", "Vacation Days", "Absences", "Complete Days", "Extra Hours", "Total Hours", "Required Hrs/Day", "Required Hours", "Avg Hours/Complete Day"];

    const lines = [header.join(",")];
    summaryRows.forEach((r) => {
      lines.push(
        [
          `"${(r.name || "").replace(/"/g, '""')}"`,
          r.email,
          r.role,
          r.workingDays,
          r.presentDays,
          r.vacationDays,
          r.absences,
          r.completeDays,
          fmtHHMM(r.extraHoursMs),
          fmtHHMM(r.totalHoursMs),
          r.requiredHoursPerDay || 0,
          fmtHHMM(r.requiredMonthlyMs),
          fmtHHMM(r.avgHoursMs),
        ].join(",")
      );
    });

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `work-hours_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [summaryRows, month]);

  /* =========================
     Daily Records (Editable existing)
     ========================= */
  const filteredDailyRecords = useMemo(() => {
    const lower = search.trim().toLowerCase();

    const withNames = attendance
      .filter((rec) => userMap.has(rec.userId))
      .map((rec) => {
        const u = userMap.get(rec.userId);
        return {
          ...rec,
          _name: u?.name || u?.email || rec.userId || "Unknown",
          _email: u?.email || "",
        };
      });

    const filtered = withNames.filter((r) => !lower || r._name.toLowerCase().includes(lower) || r._email.toLowerCase().includes(lower));

    filtered.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a._name || "").localeCompare(b._name || "");
    });

    return filtered;
  }, [attendance, userMap, search]);

  /* =========================
     Edit Existing Record Modal
     ========================= */
  const onEdit = (rec) => {
    setEditing(rec);
    setEditInTime(formatTimeInTZ(tsToDate(rec.checkInTime), TZ));
    setEditOutTime(formatTimeInTZ(tsToDate(rec.checkOutTime), TZ));
    setInfo("");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditInTime("");
    setEditOutTime("");
    setSaving(false);
    setInfo("");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    setInfo("");

    try {
      const [y, m, d] = String(editing.date).split("-").map(Number);

      let newCheckIn = null;
      let newCheckOut = null;

      if (editInTime && /^\d{2}:\d{2}$/.test(editInTime)) {
        const [hh, mm2] = editInTime.split(":").map(Number);
        newCheckIn = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, hh, mm2, TZ));
      }
      if (editOutTime && /^\d{2}:\d{2}$/.test(editOutTime)) {
        const [hh, mm2] = editOutTime.split(":").map(Number);
        newCheckOut = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, hh, mm2, TZ));
      }

      if (newCheckIn && newCheckOut && newCheckOut.toMillis() < newCheckIn.toMillis()) {
        setInfo("❗ Check-out cannot be earlier than check-in.");
        setSaving(false);
        return;
      }

      const ref = doc(db, "attendance", editing.id);
      const payload = {
        editedAt: serverTimestamp(),
        editedNote: "Manual edit via WorkHoursManagementPage",
      };

      if (editInTime === "") payload.checkInTime = deleteField();
      else if (newCheckIn) payload.checkInTime = newCheckIn;

      if (editOutTime === "") payload.checkOutTime = deleteField();
      else if (newCheckOut) payload.checkOutTime = newCheckOut;

      if (newCheckOut) payload.status = "Checked Out";
      else if (newCheckIn) payload.status = "Checked In";
      else payload.status = deleteField();

      await updateDoc(ref, payload);
      setInfo("✅ Saved.");
      await fetchAttendance();
      setTimeout(closeEdit, 250);
    } catch (e) {
      console.error("Failed to save edit", e);
      setInfo("❌ Failed to save. Please try again.");
      setSaving(false);
    }
  };

  /* =========================
     Exceptions Apply
     ========================= */
  const applyExceptions = async () => {
    setExMsg("");
    if (!exUserId) return setExMsg("Select a staff member.");
    if (!exStart || !exEnd) return setExMsg("Select a valid date range.");

    setExBusy(true);
    try {
      const user = userMap.get(exUserId);
      const rangeDates = allMonthDates.filter((ds) => ds >= exStart && ds <= exEnd);

      if (rangeDates.length === 0) {
        setExMsg("No dates in range for this month.");
        setExBusy(false);
        return;
      }

      for (const ds of rangeDates) {
        const [y, m, d] = ds.split("-").map(Number);
        if (!isWorkingDayTZ(y, m, d)) continue;

        const id = makeAttendanceId(exUserId, ds);
        const ref = doc(db, "attendance", id);

        if (exAction === "vacation") {
          await setDoc(
            ref,
            {
              userId: exUserId,
              userName: user?.name || user?.email || exUserId,
              date: ds,
              status: "Vacation",
              vacation: {
                type: exVacationType,
                note: exNote || "",
                setBy: currentUser?.uid || "system",
                setAt: serverTimestamp(),
              },
              checkInTime: deleteField(),
              checkOutTime: deleteField(),
              allowRemote: deleteField(),
              allowRemoteNote: deleteField(),
              allowRemoteSetBy: deleteField(),
              allowRemoteSetAt: deleteField(),
            },
            { merge: true }
          );
        } else if (exAction === "clearVacation") {
          await setDoc(ref, { status: deleteField(), vacation: deleteField() }, { merge: true });
        } else if (exAction === "allowRemote") {
          await setDoc(
            ref,
            {
              userId: exUserId,
              userName: user?.name || user?.email || exUserId,
              date: ds,
              allowRemote: true,
              allowRemoteNote: exNote || "",
              allowRemoteSetBy: currentUser?.uid || "system",
              allowRemoteSetAt: serverTimestamp(),
            },
            { merge: true }
          );
        } else if (exAction === "clearRemote") {
          await setDoc(
            ref,
            {
              allowRemote: deleteField(),
              allowRemoteNote: deleteField(),
              allowRemoteSetBy: deleteField(),
              allowRemoteSetAt: deleteField(),
            },
            { merge: true }
          );
        }
      }

      setExMsg("✅ Applied successfully.");
      await fetchAttendance();
    } catch (e) {
      console.error("Failed to apply exceptions", e);
      setExMsg("❌ Failed. Please try again.");
    } finally {
      setExBusy(false);
    }
  };

  /* =========================
     Quick defaults + Save Quick
     ========================= */
  useEffect(() => {
    const parts = getTZDateParts(new Date());
    const todayStr = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;

    if (todayStr.startsWith(month) && (!quickDate || !quickDate.startsWith(month))) {
      setQuickDate(todayStr);
    } else if (!quickDate) {
      setQuickDate(workingDates[0] || `${month}-01`);
    }
  }, [month, workingDates, quickDate]);

  const saveQuick = async () => {
    setQuickMsg("");
    if (!quickUserId) return setQuickMsg("Select a staff member.");
    if (!quickDate) return setQuickMsg("Select a date.");
    if (!quickIn && !quickOut) return setQuickMsg("Enter at least a check-in or a check-out time.");

    setQuickBusy(true);
    try {
      const [y, m, d] = quickDate.split("-").map(Number);

      let newCheckIn = null;
      let newCheckOut = null;

      if (quickIn && /^\d{2}:\d{2}$/.test(quickIn)) {
        const [ih, im] = quickIn.split(":").map(Number);
        newCheckIn = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, ih, im, TZ));
      }
      if (quickOut && /^\d{2}:\d{2}$/.test(quickOut)) {
        const [oh, om] = quickOut.split(":").map(Number);
        newCheckOut = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, oh, om, TZ));
      }

      if (newCheckIn && newCheckOut && newCheckOut.toMillis() < newCheckIn.toMillis()) {
        setQuickMsg("❗ Check-out cannot be earlier than check-in.");
        setQuickBusy(false);
        return;
      }

      const user = userMap.get(quickUserId);
      if (!user) {
        setQuickMsg("Selected staff is not available.");
        setQuickBusy(false);
        return;
      }

      const id = makeAttendanceId(quickUserId, quickDate);
      const payload = {
        userId: quickUserId,
        userName: user?.name || user?.email || quickUserId,
        date: quickDate,
        editedAt: serverTimestamp(),
        editedNote: "Manual add/edit via WorkHoursManagementPage quick form",
      };

      if (quickIn === "") payload.checkInTime = deleteField();
      else if (newCheckIn) payload.checkInTime = newCheckIn;

      if (quickOut === "") payload.checkOutTime = deleteField();
      else if (newCheckOut) payload.checkOutTime = newCheckOut;

      if (newCheckOut) payload.status = "Checked Out";
      else if (newCheckIn) payload.status = "Checked In";
      else payload.status = deleteField();

      payload.vacation = deleteField();

      await setDoc(doc(db, "attendance", id), payload, { merge: true });

      setQuickMsg("✅ Saved.");
      await fetchAttendance();
    } catch (e) {
      console.error("Quick save failed", e);
      setQuickMsg("❌ Failed to save. Please try again.");
    } finally {
      setQuickBusy(false);
    }
  };

  /* =========================
     Bulk
     ========================= */
  useEffect(() => {
    if (!bulkStart) setBulkStart(allMonthDates[0] || `${month}-01`);
    if (!bulkEnd) setBulkEnd(allMonthDates[allMonthDates.length - 1] || `${month}-28`);
  }, [allMonthDates, month, bulkStart, bulkEnd]);

  const toggleBulkDay = (idx) => setBulkDays((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  const setBulkWorkweek = () => setBulkDays([true, true, true, true, true, false, false]);
  const setBulkAllDays = () => setBulkDays([true, true, true, true, true, true, true]);
  const setBulkNone = () => setBulkDays([false, false, false, false, false, false, false]);

  const applyBulkHours = async () => {
    setBulkMsg("");

    if (!bulkUserId) return setBulkMsg("Select a staff member (or All staff).");
    if (!bulkStart || !bulkEnd || bulkEnd < bulkStart) return setBulkMsg("Select a valid date range.");
    if (!bulkIn && !bulkOut) return setBulkMsg("Enter at least a check-in or a check-out time.");

    if (bulkIn && bulkOut && /^\d{2}:\d{2}$/.test(bulkIn) && /^\d{2}:\d{2}$/.test(bulkOut)) {
      const [ih, im] = bulkIn.split(":").map(Number);
      const [oh, om] = bulkOut.split(":").map(Number);
      if (oh * 60 + om < ih * 60 + im) return setBulkMsg("❗ Check-out cannot be earlier than check-in.");
    }

    const targetDates = allMonthDates
      .filter((ds) => ds >= bulkStart && ds <= bulkEnd)
      .filter((ds) => {
        const partsDate = new Date(Date.UTC(Number(ds.slice(0, 4)), Number(ds.slice(5, 7)) - 1, Number(ds.slice(8, 10)), 12, 0, 0));
        const { weekdayName } = getTZDateParts(partsDate);
        const idx = weekdayIndexFromName(weekdayName);
        return bulkDays[idx];
      });

    if (targetDates.length === 0) return setBulkMsg("No dates match your day-of-week selection.");

    const targetUsers = bulkUserId === "ALL" ? users : users.filter((u) => u.id === bulkUserId);
    if (targetUsers.length === 0) return setBulkMsg("No staff found for your selection.");

    setBulkBusy(true);
    try {
      let writes = 0;

      for (const user of targetUsers) {
        for (const ds of targetDates) {
          if (!bulkOverwrite) {
            const existing = attIndex.get(user.id)?.get(ds);
            if (existing && (existing.checkInTime || existing.checkOutTime)) continue;
          }

          const [y, m, d] = ds.split("-").map(Number);

          let inTs = null;
          let outTs = null;

          if (bulkIn && /^\d{2}:\d{2}$/.test(bulkIn)) {
            const [ih, im] = bulkIn.split(":").map(Number);
            inTs = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, ih, im, TZ));
          }
          if (bulkOut && /^\d{2}:\d{2}$/.test(bulkOut)) {
            const [oh, om] = bulkOut.split(":").map(Number);
            outTs = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, oh, om, TZ));
          }

          const id = makeAttendanceId(user.id, ds);
          const payload = {
            userId: user.id,
            userName: user.name || user.email || user.id,
            date: ds,
            editedAt: serverTimestamp(),
            editedNote: "Bulk daily hours via WorkHoursManagementPage",
            vacation: deleteField(),
          };

          if (bulkOverwrite || inTs) payload.checkInTime = inTs ?? deleteField();
          if (bulkOverwrite || outTs) payload.checkOutTime = outTs ?? deleteField();

          if (outTs) payload.status = "Checked Out";
          else if (inTs) payload.status = "Checked In";
          else payload.status = deleteField();

          await setDoc(doc(db, "attendance", id), payload, { merge: true });
          writes++;
        }
      }

      setBulkMsg(`✅ Applied to ${writes} day(s).`);
      await fetchAttendance();
    } catch (e) {
      console.error("Bulk hours failed", e);
      setBulkMsg("❌ Failed to apply bulk hours. Please try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  /* =========================
     Details modal
     ========================= */
  const openDetails = (userId) => setDetailsUserId(userId);
  const closeDetails = () => setDetailsUserId(null);

  const detailsUser = detailsUserId ? userMap.get(detailsUserId) : null;

  const detailsRows = useMemo(() => {
    if (!detailsUserId) return [];
    const perDate = attIndex.get(detailsUserId) || new Map();

    return allMonthDates.map((ds) => {
      const rec = perDate.get(ds);
      const [y, m, d] = ds.split("-").map(Number);

      const inDate = tsToDate(rec?.checkInTime);
      const outDate = tsToDate(rec?.checkOutTime);
      const workedMs = inDate && outDate ? outDate - inDate : null;

      const status = rec?.status || "";
      const vacationType = rec?.vacation?.type || "";
      const vacationNote = rec?.vacation?.note || "";
      const allowRemote = !!rec?.allowRemote;
      const allowRemoteNote = rec?.allowRemoteNote || "";
      const editedNote = rec?.editedNote || "";

      const partsDate = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
      const { weekdayName } = getTZDateParts(partsDate);

      return {
        date: ds,
        weekday: weekdayName,
        status,
        vacationType,
        inTime: inDate ? formatTimeInTZ(inDate, TZ) : "",
        outTime: outDate ? formatTimeInTZ(outDate, TZ) : "",
        worked: workedMs != null && workedMs > 0 ? fmtHHMM(workedMs) : "",
        allowRemote,
        notes: [vacationNote, allowRemoteNote, editedNote].filter(Boolean).join(" | "),
      };
    });
  }, [detailsUserId, attIndex, allMonthDates]);

  const exportDetailsCSV = useCallback(() => {
    if (!detailsUserId || !detailsRows.length) return;
    const user = userMap.get(detailsUserId);

    const header = ["Date", "Weekday", "Status", "Vacation Type", "Check In (TZ)", "Check Out (TZ)", "Worked", "Remote Allowed", "Notes"];

    const lines = [header.join(",")];
    detailsRows.forEach((r) => {
      lines.push(
        [
          r.date,
          r.weekday,
          `"${(r.status || "").replace(/"/g, '""')}"`,
          r.vacationType,
          r.inTime,
          r.outTime,
          r.worked,
          r.allowRemote ? "Yes" : "No",
          `"${(r.notes || "").replace(/"/g, '""')}"`,
        ].join(",")
      );
    });

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_details_${(user?.name || user?.email || detailsUserId).replace(/\s+/g, "_")}_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [detailsUserId, detailsRows, userMap, month]);

  const printDetails = useCallback(() => window.print(), []);

  /* =========================
     ✅ NEW: Print single hours request
     ========================= */
  const printSingleHoursRequest = useCallback(
    (req) => {
      if (!req) return;

      const inD = tsToDate(req?.proposed?.checkInTime);
      const outD = tsToDate(req?.proposed?.checkOutTime);

      const html = `
        <html dir="ltr">
          <head>
            <title>Hours Request - ${req.userName || req.userEmail || req.userId}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 24px;
                color: #111827;
                background: #ffffff;
              }
              .wrap {
                max-width: 900px;
                margin: 0 auto;
              }
              h1 {
                margin: 0 0 8px;
                font-size: 24px;
              }
              .sub {
                color: #6b7280;
                margin-bottom: 24px;
              }
              .card {
                border: 1px solid #d1d5db;
                border-radius: 12px;
                padding: 18px;
                margin-bottom: 16px;
              }
              .row {
                display: grid;
                grid-template-columns: 180px 1fr;
                gap: 10px;
                margin-bottom: 10px;
              }
              .label {
                font-weight: 700;
                color: #374151;
              }
              .value {
                color: #111827;
                white-space: pre-wrap;
                word-break: break-word;
              }
              .status {
                display: inline-block;
                padding: 6px 12px;
                border-radius: 999px;
                background: #fef3c7;
                color: #92400e;
                font-weight: 700;
                font-size: 13px;
              }
              .notes {
                margin-top: 18px;
                padding-top: 12px;
                border-top: 1px dashed #d1d5db;
              }
              @media print {
                body { padding: 0; }
                .card { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="wrap">
              <h1>Employee Hours Request</h1>
              <div class="sub">Generated from Work Hours Management</div>

              <div class="card">
                <div class="row">
                  <div class="label">Employee</div>
                  <div class="value">${req.userName || req.userEmail || req.userId || "-"}</div>
                </div>

                <div class="row">
                  <div class="label">Email</div>
                  <div class="value">${req.userEmail || "-"}</div>
                </div>

                <div class="row">
                  <div class="label">Date</div>
                  <div class="value">${req.date || "-"}</div>
                </div>

                <div class="row">
                  <div class="label">Requested Check In</div>
                  <div class="value">${inD ? formatTimeInTZ(inD, TZ) : "—"}</div>
                </div>

                <div class="row">
                  <div class="label">Requested Check Out</div>
                  <div class="value">${outD ? formatTimeInTZ(outD, TZ) : "—"}</div>
                </div>

                <div class="row">
                  <div class="label">Timezone</div>
                  <div class="value">${TZ}</div>
                </div>

                <div class="row">
                  <div class="label">Status</div>
                  <div class="value"><span class="status">${req.status || "Pending"}</span></div>
                </div>

                <div class="row">
                  <div class="label">Details</div>
                  <div class="value">${req.details || "—"}</div>
                </div>

                <div class="row">
                  <div class="label">Created At</div>
                  <div class="value">${
                    req.createdAt?.toDate ? formatDisplayInTZ(req.createdAt.toDate(), TZ) : "-"
                  }</div>
                </div>

                <div class="notes">
                  <div class="row">
                    <div class="label">Manager Note</div>
                    <div class="value">${managerNote || req.decisionNote || "—"}</div>
                  </div>
                </div>
              </div>
            </div>

            <script>
              window.onload = function () {
                window.print();
                setTimeout(() => window.close(), 150);
              };
            </script>
          </body>
        </html>
      `;

      const w = window.open("", "_blank", "width=900,height=700");
      if (!w) return;

      w.document.open();
      w.document.write(html);
      w.document.close();
    },
    [managerNote]
  );

  /* =========================
     ✅ NEW: Print all hours requests
     ========================= */
  const printAllHoursRequests = useCallback(() => {
    if (!hoursReq.length) return;

    const cards = hoursReq
      .map((req) => {
        const inD = tsToDate(req?.proposed?.checkInTime);
        const outD = tsToDate(req?.proposed?.checkOutTime);

        return `
          <div class="card">
            <div class="row">
              <div class="label">Employee</div>
              <div class="value">${req.userName || req.userEmail || req.userId || "-"}</div>
            </div>

            <div class="row">
              <div class="label">Email</div>
              <div class="value">${req.userEmail || "-"}</div>
            </div>

            <div class="row">
              <div class="label">Date</div>
              <div class="value">${req.date || "-"}</div>
            </div>

            <div class="row">
              <div class="label">Requested Check In</div>
              <div class="value">${inD ? formatTimeInTZ(inD, TZ) : "—"}</div>
            </div>

            <div class="row">
              <div class="label">Requested Check Out</div>
              <div class="value">${outD ? formatTimeInTZ(outD, TZ) : "—"}</div>
            </div>

            <div class="row">
              <div class="label">Status</div>
              <div class="value"><span class="status">${req.status || "Pending"}</span></div>
            </div>

            <div class="row">
              <div class="label">Details</div>
              <div class="value">${req.details || "—"}</div>
            </div>

            <div class="row">
              <div class="label">Created At</div>
              <div class="value">${
                req.createdAt?.toDate ? formatDisplayInTZ(req.createdAt.toDate(), TZ) : "-"
              }</div>
            </div>
          </div>
        `;
      })
      .join("");

    const html = `
      <html dir="ltr">
        <head>
          <title>All Hours Requests</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #111827;
              background: #ffffff;
            }
            .wrap {
              max-width: 1000px;
              margin: 0 auto;
            }
            h1 {
              margin: 0 0 8px;
              font-size: 24px;
            }
            .sub {
              color: #6b7280;
              margin-bottom: 24px;
            }
            .card {
              border: 1px solid #d1d5db;
              border-radius: 12px;
              padding: 18px;
              margin-bottom: 16px;
              break-inside: avoid;
            }
            .row {
              display: grid;
              grid-template-columns: 180px 1fr;
              gap: 10px;
              margin-bottom: 10px;
            }
            .label {
              font-weight: 700;
              color: #374151;
            }
            .value {
              color: #111827;
              white-space: pre-wrap;
              word-break: break-word;
            }
            .status {
              display: inline-block;
              padding: 6px 12px;
              border-radius: 999px;
              background: #fef3c7;
              color: #92400e;
              font-weight: 700;
              font-size: 13px;
            }
          </style>
        </head>
        <body>
          <div class="wrap">
            <h1>All Employee Hours Requests</h1>
            <div class="sub">Pending hours requests list</div>
            ${cards}
          </div>

          <script>
            window.onload = function () {
              window.print();
              setTimeout(() => window.close(), 150);
            };
          </script>
        </body>
      </html>
    `;

    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return;

    w.document.open();
    w.document.write(html);
    w.document.close();
  }, [hoursReq]);

  /* ======================================
     ✅ UPDATED: Approve / Reject (Unified Requests)
     ====================================== */

  const approveLeave = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      const start = req.startDate;
      const end = req.endDate;
      if (!start || !end) throw new Error("Invalid leave range");

      // iterate day-by-day inclusive
      const startD = new Date(start + "T00:00:00");
      const endD = new Date(end + "T00:00:00");
      const oneDay = 24 * 60 * 60 * 1000;

      for (let t = startD.getTime(); t <= endD.getTime(); t += oneDay) {
        const d = new Date(t);
        const p = getTZDateParts(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)));
        const ds = `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
        if (!isWorkingDayTZ(p.year, p.month, p.day)) continue;

        const id = makeAttendanceId(req.userId, ds);
        await setDoc(
          doc(db, "attendance", id),
          {
            userId: req.userId,
            userName: req.userName || req.userEmail || req.userId,
            date: ds,
            status: "Vacation",
            vacation: {
              type: req.leaveType || "Paid",
              note: req.reason || "",
              setBy: currentUser?.uid || "manager",
              setAt: serverTimestamp(),
              sourceRequestId: req.id,
            },
            checkInTime: deleteField(),
            checkOutTime: deleteField(),
          },
          { merge: true }
        );
      }

      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Approved",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setManagerNote("");
      setReqMsg("✅ Leave approved & applied.");
      await fetchAttendance();
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to approve leave.");
    } finally {
      setReqLoading(false);
    }
  };

  const rejectLeave = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Rejected",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setManagerNote("");
      setReqMsg("✅ Leave rejected.");
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to reject leave.");
    } finally {
      setReqLoading(false);
    }
  };

  const openSchedule = (u) => {
    if (!u) return;
    const sch = getUserSchedule(u);
    setSchedUserId(u.id);
    setSchedUserName(u.name || u.email || u.id);
    setSchedDays(sch.workingDays);
    setSchedHours(sch.requiredHoursPerDay ? String(sch.requiredHoursPerDay) : "");
    setSchedMsg("");
    setActiveForm("schedule");
  };

  const toggleSchedDay = (idx) => {
    setSchedDays((prev) => (prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]));
  };

  const saveSchedule = async () => {
    if (!schedUserId) {
      setSchedMsg("No employee selected.");
      return;
    }
    setSchedBusy(true);
    setSchedMsg("");
    try {
      const workingDays = [...schedDays].sort((a, b) => a - b);
      const requiredHoursPerDay = Number(schedHours) || 0;
      const workSchedule = { workingDays, requiredHoursPerDay };
      await updateDoc(doc(db, "users", schedUserId), { workSchedule });
      // Reflect locally so the summary recomputes without a full refetch.
      setUsers((prev) => prev.map((u) => (u.id === schedUserId ? { ...u, workSchedule } : u)));
      setActiveForm(null);
    } catch (e) {
      console.error(e);
      setSchedMsg("❌ Failed to save schedule.");
    } finally {
      setSchedBusy(false);
    }
  };

  const approveHours = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      if (!req.userId || !req.date) throw new Error("Invalid hours request");

      const inTs = req?.proposed?.checkInTime || null;
      const outTs = req?.proposed?.checkOutTime || null;

      if (inTs && outTs && inTs.toMillis && outTs.toMillis && outTs.toMillis() < inTs.toMillis()) {
        setReqMsg("❗ Cannot approve: check-out earlier than check-in.");
        setReqLoading(false);
        return;
      }

      const attId = req.attendanceId || makeAttendanceId(req.userId, req.date);

      const payload = {
        userId: req.userId,
        userName: req.userName || req.userEmail || req.userId,
        date: req.date,
        editedAt: serverTimestamp(),
        // ✅ UPDATED: include employee reason/details in the audit note
        editedNote: `Approved hours request (${req.id})${req.details ? ` • ${req.details}` : ""}`,
        vacation: deleteField(),
        // Approving reclaims a day that was counted absent for a missed checkout — clear the
        // absence marker so it counts as a worked day again.
        cancelled: deleteField(),
        absentReason: deleteField(),
        hoursVoided: deleteField(),
        absentMarkedAt: deleteField(),
        reconsiderRequested: deleteField(),
      };

      // Apply or clear times based on proposed values
      if (req?.proposed && "checkInTime" in req.proposed) {
        payload.checkInTime = inTs ? inTs : deleteField();
      }
      if (req?.proposed && "checkOutTime" in req.proposed) {
        payload.checkOutTime = outTs ? outTs : deleteField();
      }

      if (outTs) payload.status = "Checked Out";
      else if (inTs) payload.status = "Checked In";
      else payload.status = deleteField();

      await setDoc(doc(db, "attendance", attId), payload, { merge: true });

      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Approved",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setManagerNote("");
      setReqMsg("✅ Hours approved & applied.");
      await fetchAttendance();
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to approve hours.");
    } finally {
      setReqLoading(false);
    }
  };

  const rejectHours = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Rejected",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setManagerNote("");
      setReqMsg("✅ Hours rejected.");
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to reject hours.");
    } finally {
      setReqLoading(false);
    }
  };

  // Approve extra (compensatory) hours: add the minutes onto that day's attendance record via an
  // accumulating `extraMs` field (so several approvals stack), keeping an audit log of what was
  // done. The monthly summary picks `extraMs` up and adds it to the employee's total.
  const approveExtra = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      if (!req.userId || !req.date) throw new Error("Invalid extra-hours request");
      const minutes = Number(req.minutes) || 0;
      if (minutes <= 0) throw new Error("No hours to add");

      const attId = req.attendanceId || makeAttendanceId(req.userId, req.date);
      await setDoc(
        doc(db, "attendance", attId),
        {
          userId: req.userId,
          userName: req.userName || req.userEmail || req.userId,
          date: req.date,
          extraMs: increment(minutes * 60000),
          extraLog: arrayUnion({
            minutes,
            details: req.details || "",
            workedFrom: req.workedFrom || "",
            requestId: req.id,
            approvedBy: currentUser?.uid || "manager",
            approvedAt: Timestamp.now(),
          }),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Approved",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setManagerNote("");
      setReqMsg("✅ Extra hours approved & added.");
      await fetchAttendance();
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to approve extra hours.");
    } finally {
      setReqLoading(false);
    }
  };

  const rejectExtra = async (req) => {
    setReqMsg("");
    setReqLoading(true);
    try {
      await updateDoc(doc(db, "workHoursRequests", req.id), {
        status: "Rejected",
        decisionNote: managerNote || "",
        decidedBy: currentUser?.uid || "manager",
        decidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setManagerNote("");
      setReqMsg("✅ Extra hours rejected.");
      await fetchRequests();
    } catch (e) {
      console.error(e);
      setReqMsg("❌ Failed to reject extra hours.");
    } finally {
      setReqLoading(false);
    }
  };

  /* =========================
     UI
     ========================= */
  return (
    <div className={styles.page}>
      {/* Top Header */}
      <div className={styles.topBar}>
        <div>
          <div className={styles.title}>Work Hours</div>
          <div className={styles.subtitle}>Manage attendance, vacations, requests, remote allowances & hour edits.</div>
        </div>

        <div className={styles.topControls}>
          <label className={styles.field}>
            <span>Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>

          <label className={styles.field}>
            <span>Search</span>
            <input type="text" placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>

          <button className={styles.btn} onClick={exportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <SectionButtons onOpen={openForm} pendingRequestsCount={pendingRequestsCount} />

      {/* Summary Table */}
      {error && <div className={styles.alertError}>{error}</div>}
      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.cardTitle}>Summary by Staff</div>
                <div className={styles.cardHint}>Hours = Check Out − Check In (only when both exist).</div>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Role</th>
                    <th>Working</th>
                    <th>Present</th>
                    <th>Vacation</th>
                    <th>Absences</th>
                    <th>Complete</th>
                    <th>Extra</th>
                    <th>Total</th>
                    <th>Required</th>
                    <th>Avg</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((r) => (
                    <tr key={r.userId}>
                      <td>
                        <div className={styles.personCell}>
                          <div className={styles.avatar}>{(r.name || "U")[0].toUpperCase()}</div>
                          <div>
                            <button className={styles.link} onClick={() => openDetails(r.userId)}>
                              {r.name}
                            </button>
                            <div className={styles.muted}>{r.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{r.role}</td>
                      <td>{r.workingDays}</td>
                      <td>{r.presentDays}</td>
                      <td>{r.vacationDays}</td>
                      <td className={r.absences ? styles.bad : ""}>{r.absences}</td>
                      <td>{r.completeDays}</td>
                      <td title="Approved extra / compensatory hours">
                        {r.extraHoursMs ? `+${fmtHHMM(r.extraHoursMs)}` : "—"}
                      </td>
                      <td className={r.requiredMonthlyMs && r.totalHoursMs < r.requiredMonthlyMs ? styles.bad : ""}>
                        {fmtHHMM(r.totalHoursMs)}
                      </td>
                      <td title={r.requiredHoursPerDay ? `${r.requiredHoursPerDay}h/day × ${r.workingDays} days` : "No required hours set"}>
                        {r.requiredMonthlyMs ? fmtHHMM(r.requiredMonthlyMs) : "—"}
                      </td>
                      <td>{fmtHHMM(r.avgHoursMs)}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            className={styles.btnSmall}
                            onClick={() => {
                              setActiveForm("quick");
                              setQuickUserId(r.userId);
                            }}
                          >
                            Add/Edit
                          </button>
                          <button className={styles.btnSmallGhost} onClick={() => openSchedule(userMap.get(r.userId))}>
                            Schedule
                          </button>
                          <button className={styles.btnSmallGhost} onClick={() => openDetails(r.userId)}>
                            Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {summaryRows.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ textAlign: "center", padding: 24 }}>
                        No staff match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily Records */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.cardTitle}>Daily Records</div>
                <div className={styles.cardHint}>Edit existing records (Vacation days are locked).</div>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Staff</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Worked</th>
                    <th>Flags</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDailyRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 18 }}>
                        No records in this month.
                      </td>
                    </tr>
                  ) : (
                    filteredDailyRecords.map((rec) => {
                      const inDate = tsToDate(rec.checkInTime);
                      const outDate = tsToDate(rec.checkOutTime);
                      const workedMs = inDate && outDate ? outDate - inDate : null;

                      const u = userMap.get(rec.userId);
                      const name = u?.name || u?.email || rec.userId || "Unknown";

                      return (
                        <tr key={rec.id}>
                          <td>{rec.date}</td>
                          <td>
                            <div className={styles.personCell}>
                              <div className={styles.avatar}>{name[0].toUpperCase()}</div>
                              <div>
                                <div className={styles.textStrong}>{name}</div>
                                <div className={styles.muted}>{u?.email || ""}</div>
                              </div>
                            </div>
                          </td>
                          <td>{inDate ? formatDisplayInTZ(inDate, TZ) : "-"}</td>
                          <td>{outDate ? formatDisplayInTZ(outDate, TZ) : "-"}</td>
                          <td>{workedMs != null && workedMs > 0 ? fmtHHMM(workedMs) : "-"}</td>
                          <td>
                            <div className={styles.badges}>
                              {rec.status === "Vacation" && (
                                <span className={styles.badgeInfo}>
                                  Vacation{rec?.vacation?.type ? ` (${rec.vacation.type})` : ""}
                                </span>
                              )}
                              {rec.allowRemote && <span className={styles.badge}>Remote</span>}
                            </div>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button className={styles.btnSmall} onClick={() => onEdit(rec)} disabled={rec.status === "Vacation"}>
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* =========================
          FORMS as Popups (Modals)
         ========================= */}

      {/* Requests Modal */}
      <Modal
        open={activeForm === "requests"}
        title="Requests"
        subtitle="Approve / Reject leave & hours requests"
        onClose={closeForm}
        footer={
          <>
            {reqTab === "hours" && hoursReq.length > 0 && (
              <button className={styles.btnGhost} onClick={printAllHoursRequests} type="button">
                Print Hours Requests
              </button>
            )}

            <button className={styles.btnGhost} onClick={closeForm} disabled={reqLoading}>
              Close
            </button>
            <button className={styles.btnPrimary} onClick={fetchRequests} disabled={reqLoading}>
              {reqLoading ? "Loading…" : "Refresh"}
            </button>
          </>
        }
      >
        <div className={styles.reqTabs}>
          <button className={`${styles.reqTab} ${reqTab === "leave" ? styles.reqTabActive : ""}`} onClick={() => setReqTab("leave")} type="button">
            Leave ({leaveReq.length})
          </button>
          <button className={`${styles.reqTab} ${reqTab === "hours" ? styles.reqTabActive : ""}`} onClick={() => setReqTab("hours")} type="button">
            Hours ({hoursReq.length})
          </button>
          <button className={`${styles.reqTab} ${reqTab === "extra" ? styles.reqTabActive : ""}`} onClick={() => setReqTab("extra")} type="button">
            Extra ({extraReq.length})
          </button>
        </div>

        {reqMsg && <div className={styles.alertInfo}>{reqMsg}</div>}

        <div className={styles.field} style={{ marginTop: 10 }}>
          <span>Manager Note (optional)</span>
          <input value={managerNote} onChange={(e) => setManagerNote(e.target.value)} placeholder="Reason for approval/rejection…" />
        </div>

        <div className={styles.reqList}>
          {reqTab === "leave" ? (
            leaveReq.length === 0 ? (
              <div className={styles.empty}>No pending leave requests.</div>
            ) : (
              leaveReq.map((r) => (
                <div key={r.id} className={styles.reqItem}>
                  <div className={styles.reqTop}>
                    <div className={styles.reqTitle}>
                      {r.userName || r.userEmail || r.userId} — {r.leaveType || "Paid"}
                    </div>
                    <span className={styles.badgeInfo}>Pending</span>
                  </div>

                  <div className={styles.reqMeta}>
                    <div className={styles.reqLine}>
                      <strong>Range:</strong> {r.startDate} → {r.endDate}
                    </div>
                    <div className={styles.reqLine}>
                      <strong>Reason:</strong> {r.reason || "—"}
                    </div>
                  </div>

                  <div className={styles.reqActions}>
                    <button className={styles.btnSmallGhost} onClick={() => rejectLeave(r)} disabled={reqLoading}>
                      Reject
                    </button>
                    <button className={styles.btnSmall} onClick={() => approveLeave(r)} disabled={reqLoading}>
                      Approve & Apply
                    </button>
                  </div>
                </div>
              ))
            )
          ) : reqTab === "extra" ? (
            extraReq.length === 0 ? (
              <div className={styles.empty}>No pending extra-hours requests.</div>
            ) : (
              extraReq.map((r) => (
                <div key={r.id} className={styles.reqItem}>
                  <div className={styles.reqTop}>
                    <div className={styles.reqTitle}>{r.userName || r.userEmail || r.userId}</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span
                        style={{
                          background: "#ecfeff", color: "#0e7490", border: "1px solid #a5f3fc",
                          borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700,
                        }}
                      >
                        Extra hours
                      </span>
                      <span className={styles.badgeInfo}>Pending</span>
                    </div>
                  </div>

                  <div className={styles.reqMeta}>
                    <div className={styles.reqLine}>
                      <strong>Date:</strong> {r.date}
                    </div>
                    <div className={styles.reqLine}>
                      <strong>Hours to add:</strong> {fmtHHMM((Number(r.minutes) || 0) * 60000)}
                      {r.workedFrom ? ` • from ${r.workedFrom}` : ""}
                    </div>
                    <div className={styles.reqLine}>
                      <strong>What was done:</strong> {r.details || "—"}
                    </div>
                  </div>

                  <div className={styles.reqActions}>
                    <button className={styles.btnSmallGhost} onClick={() => rejectExtra(r)} disabled={reqLoading}>
                      Reject
                    </button>
                    <button className={styles.btnSmall} onClick={() => approveExtra(r)} disabled={reqLoading}>
                      Approve &amp; Add
                    </button>
                  </div>
                </div>
              ))
            )
          ) : hoursReq.length === 0 ? (
            <div className={styles.empty}>No pending hours requests.</div>
          ) : (
            hoursReq.map((r) => {
              const inD = tsToDate(r?.proposed?.checkInTime);
              const outD = tsToDate(r?.proposed?.checkOutTime);

              return (
                <div key={r.id} className={styles.reqItem}>
                  <div className={styles.reqTop}>
                    <div className={styles.reqTitle}>{r.userName || r.userEmail || r.userId}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {r.reason === 'missed_checkout' && (
                        <span
                          style={{
                            background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                            borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 700,
                          }}
                          title="Missed checkout — employee is requesting the day be counted as worked"
                        >
                          Missed checkout
                        </span>
                      )}
                      <span className={styles.badgeInfo}>Pending</span>
                    </div>
                  </div>

                  <div className={styles.reqMeta}>
                    <div className={styles.reqLine}>
                      <strong>Date:</strong> {r.date}
                    </div>
                    <div className={styles.reqLine}>
                      <strong>Times:</strong> {inD ? formatTimeInTZ(inD, TZ) : "—"} → {outD ? formatTimeInTZ(outD, TZ) : "—"} ({TZ})
                    </div>

                    {/* ✅ NEW: show employee reason/details */}
                    <div className={styles.reqLine}>
                      <strong>Details:</strong> {r.details ? r.details : "—"}
                    </div>
                  </div>

                  <div className={styles.reqActions}>
                    <button
                      className={styles.btnSmallGhost}
                      onClick={() => printSingleHoursRequest(r)}
                      type="button"
                    >
                      Print
                    </button>

                    <button className={styles.btnSmallGhost} onClick={() => rejectHours(r)} disabled={reqLoading}>
                      Reject
                    </button>
                    <button className={styles.btnSmall} onClick={() => approveHours(r)} disabled={reqLoading}>
                      Approve & Apply
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>

      {/* Exceptions Form Modal */}
      <Modal
        open={activeForm === "exceptions"}
        title="Exceptions & Allowances"
        subtitle="Vacation / Remote attendance by date range"
        onClose={closeForm}
        footer={
          <>
            <button className={styles.btnGhost} onClick={closeForm} disabled={exBusy}>
              Close
            </button>
            <button className={styles.btnPrimary} onClick={applyExceptions} disabled={exBusy}>
              {exBusy ? "Applying…" : "Apply"}
            </button>
          </>
        }
      >
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Staff</span>
            <select value={exUserId} onChange={(e) => setExUserId(e.target.value)}>
              <option value="">— Select staff —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.email ? `(${u.email})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Action</span>
            <select value={exAction} onChange={(e) => setExAction(e.target.value)}>
              <option value="vacation">Mark Vacation (range)</option>
              <option value="clearVacation">Clear Vacation (range)</option>
              <option value="allowRemote">Allow Online Attendance (range)</option>
              <option value="clearRemote">Clear Online Attendance (range)</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Start</span>
            <input type="date" value={exStart} onChange={(e) => setExStart(e.target.value)} />
          </label>

          <label className={styles.field}>
            <span>End</span>
            <input type="date" value={exEnd} onChange={(e) => setExEnd(e.target.value)} />
          </label>

          {exAction === "vacation" && (
            <label className={styles.field}>
              <span>Vacation Type</span>
              <select value={exVacationType} onChange={(e) => setExVacationType(e.target.value)}>
                <option value="Paid">Paid</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </label>
          )}

          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span>Note</span>
            <input type="text" placeholder="Optional…" value={exNote} onChange={(e) => setExNote(e.target.value)} />
          </label>

          {exMsg && (
            <div className={styles.alertInfo} style={{ gridColumn: "1 / -1" }}>
              {exMsg}
            </div>
          )}
        </div>
      </Modal>

      {/* Quick Add/Edit Modal */}
      <Modal
        open={activeForm === "quick"}
        title="Add / Edit Hours"
        subtitle="Set check-in / check-out for a single date"
        onClose={closeForm}
        footer={
          <>
            <button className={styles.btnGhost} onClick={closeForm} disabled={quickBusy}>
              Close
            </button>
            <button className={styles.btnPrimary} onClick={saveQuick} disabled={quickBusy}>
              {quickBusy ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Staff</span>
            <select value={quickUserId} onChange={(e) => setQuickUserId(e.target.value)}>
              <option value="">— Select staff —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.email ? `(${u.email})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Date</span>
            <input
              type="date"
              value={quickDate}
              onChange={(e) => setQuickDate(e.target.value)}
              min={`${month}-01`}
              max={allMonthDates[allMonthDates.length - 1] || `${month}-28`}
            />
          </label>

          <label className={styles.field}>
            <span>Check In</span>
            <input type="time" value={quickIn} onChange={(e) => setQuickIn(e.target.value)} />
          </label>

          <label className={styles.field}>
            <span>Check Out</span>
            <input type="time" value={quickOut} onChange={(e) => setQuickOut(e.target.value)} />
          </label>

          {quickMsg && (
            <div className={styles.alertInfo} style={{ gridColumn: "1 / -1" }}>
              {quickMsg}
            </div>
          )}
        </div>
      </Modal>

      {/* Schedule Modal — per-employee working days + required hours */}
      <Modal
        open={activeForm === "schedule"}
        title="Work Schedule"
        subtitle={schedUserName ? `Working days & required hours for ${schedUserName}` : "Working days & required hours"}
        onClose={closeForm}
        footer={
          <>
            <button className={styles.btnGhost} onClick={closeForm} disabled={schedBusy}>
              Close
            </button>
            <button className={styles.btnPrimary} onClick={saveSchedule} disabled={schedBusy}>
              {schedBusy ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className={styles.formGrid}>
          <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span>Working days</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {WEEKDAYS.map((w) => {
                const on = schedDays.includes(w.idx);
                return (
                  <label
                    key={w.idx}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      border: `1px solid ${on ? "#4f46e5" : "#d1d5db"}`,
                      background: on ? "#eef2ff" : "#fff",
                      color: on ? "#3730a3" : "#374151",
                      borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 600,
                    }}
                  >
                    <input type="checkbox" checked={on} onChange={() => toggleSchedDay(w.idx)} />
                    {w.short}
                  </label>
                );
              })}
            </div>
          </div>

          <label className={styles.field}>
            <span>Required hours / day</span>
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={schedHours}
              onChange={(e) => setSchedHours(e.target.value)}
              placeholder="e.g. 8"
            />
          </label>

          <div className={styles.field} style={{ justifyContent: "flex-end" }}>
            <span>&nbsp;</span>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {schedDays.length} working day(s) selected
              {Number(schedHours) > 0 ? ` • ${(schedDays.length * Number(schedHours)).toFixed(1)}h / week` : ""}
            </div>
          </div>

          {schedMsg && (
            <div className={styles.alertInfo} style={{ gridColumn: "1 / -1" }}>
              {schedMsg}
            </div>
          )}
        </div>
      </Modal>

      {/* Bulk Modal */}
      <Modal
        open={activeForm === "bulk"}
        title="Bulk Daily Hours"
        subtitle="Fill hours for many days (single staff or all staff)"
        onClose={closeForm}
        footer={
          <>
            <button className={styles.btnGhost} onClick={closeForm} disabled={bulkBusy}>
              Close
            </button>
            <button className={styles.btnPrimary} onClick={applyBulkHours} disabled={bulkBusy}>
              {bulkBusy ? "Applying…" : "Apply"}
            </button>
          </>
        }
      >
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Staff</span>
            <select value={bulkUserId} onChange={(e) => setBulkUserId(e.target.value)}>
              <option value="">— Select staff —</option>
              <option value="ALL">All staff</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.email ? `(${u.email})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Start</span>
            <input type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} min={allMonthDates[0] || `${month}-01`} max={allMonthDates[allMonthDates.length - 1] || `${month}-28`} />
          </label>

          <label className={styles.field}>
            <span>End</span>
            <input type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} min={bulkStart || allMonthDates[0] || `${month}-01`} max={allMonthDates[allMonthDates.length - 1] || `${month}-28`} />
          </label>

          <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span>Days</span>
            <div className={styles.weekRow}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((lbl, i) => (
                <label key={lbl} className={styles.chk}>
                  <input type="checkbox" checked={bulkDays[i]} onChange={() => toggleBulkDay(i)} />
                  <span>{lbl}</span>
                </label>
              ))}
              <div className={styles.weekTools}>
                <button type="button" className={styles.linkBtn} onClick={setBulkWorkweek}>
                  Workweek
                </button>
                <button type="button" className={styles.linkBtn} onClick={setBulkAllDays}>
                  All
                </button>
                <button type="button" className={styles.linkBtn} onClick={setBulkNone}>
                  None
                </button>
              </div>
            </div>
          </div>

          <label className={styles.field}>
            <span>Check In</span>
            <input type="time" value={bulkIn} onChange={(e) => setBulkIn(e.target.value)} />
          </label>

          <label className={styles.field}>
            <span>Check Out</span>
            <input type="time" value={bulkOut} onChange={(e) => setBulkOut(e.target.value)} />
          </label>

          <label className={styles.field}>
            <span>Mode</span>
            <div className={styles.inline}>
              <label className={styles.chk}>
                <input type="checkbox" checked={bulkOverwrite} onChange={(e) => setBulkOverwrite(e.target.checked)} />
                <span>Overwrite existing</span>
              </label>
            </div>
          </label>

          {bulkMsg && (
            <div className={styles.alertInfo} style={{ gridColumn: "1 / -1" }}>
              {bulkMsg}
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Existing Record Modal */}
      <Modal
        open={!!editing}
        title="Edit Times"
        subtitle={
          editing
            ? `${userMap.get(editing.userId)?.name || userMap.get(editing.userId)?.email || editing.userId} — ${editing.date}`
            : ""
        }
        onClose={closeEdit}
        footer={
          <>
            <button className={styles.btnGhost} onClick={closeEdit} disabled={saving}>
              Cancel
            </button>
            <button className={styles.btnPrimary} onClick={saveEdit} disabled={saving || editing?.status === "Vacation"}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {editing?.status === "Vacation" && (
          <div className={styles.alertInfo} style={{ marginBottom: 12 }}>
            This day is marked as <strong>Vacation</strong>. Clear it in Exceptions to edit times.
          </div>
        )}

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Check In</span>
            <input type="time" value={editInTime} onChange={(e) => setEditInTime(e.target.value)} disabled={editing?.status === "Vacation"} />
          </label>

          <label className={styles.field}>
            <span>Check Out</span>
            <input type="time" value={editOutTime} onChange={(e) => setEditOutTime(e.target.value)} disabled={editing?.status === "Vacation"} />
          </label>

          {info && (
            <div className={styles.alertInfo} style={{ gridColumn: "1 / -1" }}>
              {info}
            </div>
          )}
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        open={!!detailsUserId}
        title={`${detailsUser?.name || detailsUser?.email || detailsUserId || ""} — ${month}`}
        subtitle="Month details (times shown in Asia/Jerusalem)"
        onClose={closeDetails}
        footer={
          <>
            <button className={styles.btnGhost} onClick={printDetails}>
              Print
            </button>
            <button className={styles.btnGhost} onClick={exportDetailsCSV}>
              Export CSV
            </button>
            <button className={styles.btnPrimary} onClick={closeDetails}>
              Close
            </button>
          </>
        }
      >
        <div className={styles.tableWrap} style={{ maxHeight: 520 }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Weekday</th>
                <th>Status</th>
                <th>Vacation</th>
                <th>In</th>
                <th>Out</th>
                <th>Worked</th>
                <th>Remote</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {detailsRows.map((r) => (
                <tr key={r.date}>
                  <td>{r.date}</td>
                  <td>{r.weekday}</td>
                  <td>{r.status || "-"}</td>
                  <td>{r.vacationType || "-"}</td>
                  <td>{r.inTime || "-"}</td>
                  <td>{r.outTime || "-"}</td>
                  <td>{r.worked || "-"}</td>
                  <td>{r.allowRemote ? "Yes" : "No"}</td>
                  <td>{r.notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}